import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import { SmartSoftButton } from "soft_digi";
import * as XLSX from "xlsx";
import { CONSUMPTION_URL } from "../../api/UserUrls";
import { useSiteContext } from "../../contexts/SiteProvider";
import { changeDateTimeZone, roundNumber } from "../../services/core/CommonService";
import { isDateWithinDays } from "../../services/site/DateService";
import { post } from "../../services/smartApiService";
import VendorRatesTable from "../VendorRates/VendorRatesTable";
import ConsumptionReportForm from "./ConsumptionReportForm";
import ImportReportTable from "./ImportReportTable";

interface VehicleReportProps {
  stage: any;
  setStage: any;
}

const ConsumptionAdminReport: React.FC<VehicleReportProps> = ({ stage, setStage }) => {
  const { openModal } = useSiteContext();

  const [numberArray, setNumberArray] = useState<any[]>([]);
  const [data, setData]               = useState<any[]>([]);
  const [startDate, setStartDate]     = useState(moment().date(21).startOf("day"));
  const [endDate, setEndDate]         = useState(moment().add(1, "month").date(20).endOf("day"));
  const [searchTerm, setSearchTerm]   = useState<string>("");

  const handelStage = () => setStage("HUB");

  const loadData = () => {
    const _data = {
      start_date: changeDateTimeZone(startDate.toISOString(), "YYYY-MM-DD"),
      end_date:   changeDateTimeZone(endDate.toISOString(),   "YYYY-MM-DD"),
    };
    const subscription = post(CONSUMPTION_URL.GET_ALL_WITH_HUB, _data).subscribe((response) => {
      setData(response.data.data);
      setNumberArray(response.data.dates);
    });
    return () => subscription.unsubscribe();
  };

  useEffect(() => { loadData(); }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value);

  const filteredData = data.filter(
    (item) =>
      item.hub_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.office_city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Date range picker ──────────────────────────────────────
  const dateRange = () => (
    <div className="is-flex is-align-items-center">
      <input
        type="date"
        className="input is-small mr-2"
        value={startDate.format("YYYY-MM-DD")}
        onChange={(e) => {
          const newStart = moment(e.target.value).startOf("day");
          setStartDate(newStart);
          if (endDate.isBefore(newStart)) setEndDate(newStart.clone().endOf("day"));
        }}
      />
      <span className="mx-2">to</span>
      <input
        type="date"
        className="input is-small mr-2"
        min={startDate.format("YYYY-MM-DD")}
        value={endDate.format("YYYY-MM-DD")}
        onChange={(e) => setEndDate(moment(e.target.value).endOf("day"))}
      />
      <SmartSoftButton
        label="Go"
        classList={["button", "is-link", "is-small", "px-4"]}
        onClick={() => loadData()}
      />
    </div>
  );

  // ── Cell helpers ───────────────────────────────────────────
  const getDayCount = (date: string, subData: any[], extra_units = 0) => {
    if (subData?.length > 0) {
      const obj = subData.find((o) => o.date === date && o.extra_units === extra_units);
      return obj?.count ?? 0;
    }
    return 0;
  };

  // ── Modal openers ──────────────────────────────────────────
  const openForm = (date: any, hub: any, endDate?: any, extra = false) => {
    openModal({
      title: (
        <div>
          Hub: {hub?.hub_id}
          <span className="has-text-black ml-6">
            Date: {changeDateTimeZone(date, "DD-MM-YYYY")}
          </span>
        </div>
      ),
      className: "sd-efl-modal",
      closeBody: false,
      content: (
        <ConsumptionReportForm
          loadTableData={loadData}
          date={date}
          hub_id={{ value: hub.ID }}
          endDate={endDate}
          extra={extra}
        />
      ),
    });
  };

  const openImportForm = () => {
    openModal({
      title: "Import Data",
      content: <ImportReportTable loadTableData={loadData} />,
      className: "sd-efl-modal",
      closeBody: false,
    });
  };

  const openVendorsView = (hubId: any) => {
    openModal({
      title: "",
      content: <VendorRatesTable hubId={hubId} />,
      width: 80,
    });
  };

  const exportToExcel = () => {
    const headers = [
      "S.NO",
      "HUB NAME",
      "CITY",
      "MR",
      "TOTAL",
      "DEVIATION",
      "EXTRA",
      ...numberArray.map((item: any) => changeDateTimeZone(item, "DD-MMM")),
    ];

    const rows = filteredData.map((hub, idx) => [
      idx + 1,
      hub.hub_name,
      hub.office_city,
      hub.meter_reading,
      roundNumber(hub.total),
      hub.deviation,
      getDayCount(
        changeDateTimeZone(startDate.toISOString(), "YYYY-MM-DD"),
        hub.sub_data,
        1
      ),
      ...numberArray.map((item: any) => {
        const count = getDayCount(item, hub.sub_data);
        return count > 0 ? roundNumber(count) : "";
      }),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Consumption Report");
    XLSX.writeFile(
      wb,
      `consumption_report_${startDate.format("DD-MM-YYYY")}_to_${endDate.format("DD-MM-YYYY")}.xlsx`
    );
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="efl-report-card">

      {/* ── Top control row ── */}
      <div className="efl-report-header-row columns is-vcentered is-multiline">

        {/* Title */}
        <div className="column is-narrow">
          <span className="efl-report-title">Consumption Report</span>
        </div>

        {/* Actions */}
        <div className="column">
          <div className="is-flex is-align-items-center" style={{ gap: "8px" }}>
            {/* Import icon */}
            <span
              className="has-text-link is-clickable"
              title="Import"
              onClick={() => openImportForm()}
            >
              <i className="fa fa-upload is-size-4"></i>
            </span>

            {/* Export icon */}
            <span
              className="has-text-success is-clickable"
              title="Export to Excel"
              onClick={exportToExcel}
            >
              <i className="fa fa-file-excel-o is-size-4"></i>
            </span>

            {/* Search */}
            <div className="search-box sd-efl-input">
              <input
                className="input"
                type="text"
                placeholder="Search hub or city…"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>


            {/* Refresh */}
            <span
              className="is-size-4 smart-refresh-icon is-clickable"
              onClick={loadData}
            >
              <i className="fa fa-refresh"></i>
            </span>
          </div>
        </div>

        {/* Date range */}
        <div className="column is-narrow">
          {dateRange()}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="scrollable-table">
        <table className="table is-bordered is-fullwidth smart-report-table">
          <thead>
            <tr>
              <th>Hub Name</th>
              <th>MR</th>
              <th>Total</th>
              <th>Deviation</th>
              <th>Extra</th>
              {numberArray.map((item: any, idx: number) => (
                <th key={idx}>{changeDateTimeZone(item, "DD")}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {filteredData.map((hub, rowIdx) => (
              <tr key={rowIdx}>
                {/* Hub name cell */}
                <td>
                  <div className="is-flex is-align-items-center" style={{ gap: "4px" }}>
                    <span className="efl-hub-name">{hub.hub_name}</span>
                    {hub.vendor_count !== 0 ? (
                      <span
                        className="efl-hub-count"
                        onClick={() => openVendorsView(hub.ID)}
                      >
                        ({roundNumber(hub.vendor_count)})
                      </span>
                    ) : (
                      <span style={{ fontSize: "12px", color: "#718096" }}>
                        ({roundNumber(hub.vendor_count)})
                      </span>
                    )}
                  </div>
                  <div className="efl-hub-city">({hub.office_city})</div>
                </td>

                {/* MR */}
                <td>{hub.meter_reading}</td>

                {/* Total */}
                <td
                  className="is-clickable"
                  onClick={() =>
                    openForm(
                      changeDateTimeZone(startDate.toISOString(), "YYYY-MM-DD"),
                      hub,
                      changeDateTimeZone(endDate.toISOString(), "YYYY-MM-DD")
                    )
                  }
                >
                  <span className="is-clickable has-text-link">
                    {roundNumber(hub.total)}
                  </span>
                </td>

                {/* Deviation */}
                <td>{hub.deviation}</td>

                {/* Extra */}
                <td
                  className="is-clickable"
                  onClick={() =>
                    openForm(
                      changeDateTimeZone(startDate.toISOString(), "YYYY-MM-DD"),
                      hub,
                      undefined,
                      true
                    )
                  }
                >
                  <span className="has-text-link">
                    {getDayCount(
                      changeDateTimeZone(startDate.toISOString(), "YYYY-MM-DD"),
                      hub.sub_data,
                      1
                    )}
                  </span>
                </td>

                {/* Daily columns */}
                {numberArray.map((item: any, colIdx: number) => {
                  const _count = getDayCount(item, hub.sub_data);
                  const canAdd  = isDateWithinDays(item, 0);

                  return _count > 0 ? (
                    <td key={colIdx}>
                      <span
                        className="sd-cursor has-text-danger"
                        onClick={() => openForm(item, hub)}
                      >
                        {roundNumber(_count)}
                      </span>
                    </td>
                  ) : (
                    <td key={colIdx}>
                      {canAdd && (
                        <span
                          className="sd-cursor has-text-link is-size-5"
                          onClick={() => openForm(item, hub)}
                        >
                          +
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsumptionAdminReport;
